"use client";

import posthog from "posthog-js";
import { useEffect } from "react";

/**
 * Global error boundary for the root layout.
 * This catches errors that occur in the root layout itself.
 * 
 * Note: global-error must include <html> and <body> tags since it replaces the entire page.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Capture the error to PostHog
    try {
      posthog.captureException(error, {
        $error_boundary: "global",
        $error_digest: error.digest,
      });
    } catch (captureError) {
      console.error("[Global Error] Failed to capture error:", captureError);
    }

    // Also log to console for debugging
    console.error("[Global Error] Root layout error:", error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "2rem",
          backgroundColor: "#0a0a0a",
          color: "#ffffff",
          fontFamily:
            "var(--font-inter, Inter), Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: "500px",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              marginBottom: "1rem",
              color: "#ef4444",
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              color: "#a1a1aa",
              marginBottom: "1.5rem",
              lineHeight: 1.6,
            }}
          >
            A critical error occurred. We've been notified and are working to fix it.
            Please refresh the page or try again later.
          </p>
          {process.env.NODE_ENV === "development" && (
            <pre
              style={{
                backgroundColor: "#18181b",
                padding: "1rem",
                borderRadius: "0.5rem",
                fontSize: "0.75rem",
                textAlign: "left",
                overflow: "auto",
                marginBottom: "1.5rem",
                color: "#ef4444",
                maxHeight: "200px",
              }}
            >
              {error.message}
              {"\n\n"}
              {error.stack}
            </pre>
          )}
          <button
            onClick={reset}
            style={{
              backgroundColor: "#3b82f6",
              color: "#ffffff",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.5rem",
              border: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
